import { convertExportHtmlToPdf } from '../service/form';

type ScsBusinessObjective = {
  number: number;
  what: string;
  how: string;
};

const answerText = (value: unknown): string => {
  if (value === undefined || value === null) return '';
  if (Array.isArray(value)) return value.map(answerText).filter(Boolean).join(', ');
  return String(value);
};

const escapeHtml = (value: unknown): string =>
  answerText(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const toFilenamePart = (value: unknown): string =>
  answerText(value)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^[_-]+|[_-]+$/g, '');

export const getScsObjectivesPdfFilename = (
  answers: Record<string, unknown>,
  formId?: string,
): string => {
  const scsName = toFilenamePart(answers['person-details-scs-name']);
  const reportingPeriod = toFilenamePart(answers['report-period']);
  if (!scsName || !reportingPeriod) {
    const fallbackFormId = formId?.trim();
    if (!fallbackFormId) throw new Error('Form ID is required for the objectives PDF filename');
    return `${fallbackFormId}.pdf`;
  }
  return `${scsName}_${reportingPeriod}_scs_objectives.pdf`;
};

export const getScsBusinessObjectives = (
  answers: Record<string, unknown>,
): ScsBusinessObjective[] => {
  const objectiveNumbers = new Set<number>();

  Object.keys(answers).forEach((key) => {
    const match = key.match(/^(\d+)-business-objective-(?:what|how)$/);
    if (match) objectiveNumbers.add(Number(match[1]));
  });

  return Array.from(objectiveNumbers)
    .sort((left, right) => left - right)
    .map((answerNumber, index) => ({
      number: index + 1,
      what: answerText(answers[`${answerNumber}-business-objective-what`]),
      how: answerText(answers[`${answerNumber}-business-objective-how`]),
    }));
};

export const buildScsObjectivesPdfHtml = (answers: Record<string, unknown>): string => {
  const objectives = getScsBusinessObjectives(answers);
  const businessUnit =
    answerText(answers['business-area']) === 'Other / Not listed' &&
    answerText(answers['person-details-business-unit-other']).trim()
      ? answers['person-details-business-unit-other']
      : answers['business-area'];
  const objectivesHtml = objectives
    .map(
      ({ number, what, how }) => `
        <section class="objective">
          <h2>Business Objective ${number}</h2>
          <table>
            <thead>
              <tr>
                <th scope="col">Business Objective - What</th>
                <th scope="col">Business Objective - How</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>${escapeHtml(what)}</td>
                <td>${escapeHtml(how)}</td>
              </tr>
            </tbody>
          </table>
        </section>`,
    )
    .join('');

  return `<!doctype html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <title>SCS Objectives</title>
    <style>
      @page { size: A4; margin: 18mm; }
      * { box-sizing: border-box; }
      body {
        color: #0b0c0c;
        font-family: Arial, Helvetica, sans-serif;
        font-size: 12pt;
        line-height: 1.45;
        margin: 0;
      }
      h1 { font-size: 24pt; margin: 0 0 22px; }
      h2 { font-size: 17pt; margin: 28px 0 10px; }
      table { border-collapse: collapse; table-layout: fixed; width: 100%; }
      th, td {
        border: 1px solid #b1b4b6;
        padding: 10px;
        text-align: left;
        vertical-align: top;
        white-space: pre-wrap;
        overflow-wrap: anywhere;
      }
      th { background: #f3f2f1; font-weight: 700; }
      .details th { width: 34%; }
      .objective { break-inside: avoid; page-break-inside: avoid; }
    </style>
  </head>
  <body>
    <h1>SCS Objectives</h1>
    <table class="details">
      <tbody>
        <tr><th scope="row">Reporting Period</th><td>${escapeHtml(answers['report-period'])}</td></tr>
        <tr><th scope="row">SCS Name</th><td>${escapeHtml(answers['person-details-scs-name'])}</td></tr>
        <tr><th scope="row">Job Title</th><td>${escapeHtml(answers['person-details-job-title'])}</td></tr>
        <tr><th scope="row">Business Unit</th><td>${escapeHtml(businessUnit)}</td></tr>
      </tbody>
    </table>
    ${objectivesHtml}
  </body>
</html>`;
};

export const createScsObjectivesPdf = async (
  token: string,
  answers: Record<string, unknown>,
  formVersion?: number,
  formId?: string,
) => {
  const filename = getScsObjectivesPdfFilename(answers, formId);
  const result = await convertExportHtmlToPdf(token, {
    html: buildScsObjectivesPdfHtml(answers),
    filename: filename.replace(/\.pdf$/i, ''),
    formVersion,
  });
  return { ...result, filename };
};
